/**
 * Pool of taglines and hashtag-style tags for the procedural author profile
 * tooltip (Variant B only). The seeded RNG picks an INDEX once, and that
 * index is then resolved against whichever language pool is active at render
 * time — so the same seed reads as the same logical author whether the
 * participant has the UI in DE or EN.
 *
 * Position parity (EN[i] and DE[i] are the same logical entry) is the load-
 * bearing invariant — never reorder one side without reordering the other.
 *
 * Taglines stay ≤ ~40 chars so the 140 px tooltip card never has to wrap
 * more than two lines. Tags stay ≤ 10 chars (no leading `#` — the renderer
 * adds it) and avoid umlauts (DE uses `ae`/`oe`/`ue` since the on-screen
 * font for tags is the small monospace HUD face).
 */

import type { Language } from "../i18n";

export const AUTHOR_BIO_TAGLINES_EN: readonly string[] = [
  "Maps the ruins for the rest of us.",
  "Sells what falls out of pockets.",
  "Charts every door, opens half.",
  "Speedrunner turned shopkeeper.",
  "Never met a feather she didn't keep.",
  "Knows where the geo hides.",
  "Trades cheap. Sometimes.",
  "Builds towers, leaves the doors open.",
  "Walks the floor so you don't have to.",
  "One scroll, one secret.",
  "Reads the lanterns like signs.",
  "Two jumps short of legend.",
  "Found the path. Forgot the map.",
  "Will trade boots for stories.",
  "Carries more than seems wise.",
  "Sells routes by the chapter.",
  "Lights the lamp on the way out.",
  "Better at landing than starting.",
  "Lost on purpose; arriving anyway.",
  "Counts ghosts, names a few.",
  "Knows which gates lie.",
  "Old-timer of the green halls.",
  "Splits the feather, shares the climb.",
  "Has opinions about scroll prices.",
  "Sketches doors that aren't there yet.",
  "Asks the candle, follows the smoke.",
  "Picks up bones, sets down lanterns.",
  "Sees doubles. Jumps anyway.",
  "Stays for the puzzles.",
  "Quiet trader. Patient walker.",
  "Names her routes after weather.",
  "Buys ruins, sells rumours.",
];

export const AUTHOR_BIO_TAGLINES_DE: readonly string[] = [
  "Kartiert die Ruinen für uns alle.",
  "Verkauft, was aus den Taschen fällt.",
  "Zeichnet jede Tür, öffnet die Hälfte.",
  "Vom Speedrunner zum Krämer.",
  "Lässt keine Feder liegen.",
  "Weiß, wo das Geo versteckt ist.",
  "Tauscht günstig. Manchmal.",
  "Baut Türme, lässt Türen offen.",
  "Läuft den Boden, damit du es nicht musst.",
  "Eine Schriftrolle, ein Geheimnis.",
  "Liest Laternen wie Wegweiser.",
  "Zwei Sprünge fehlen zur Legende.",
  "Pfad gefunden. Karte vergessen.",
  "Tauscht Stiefel gegen Geschichten.",
  "Trägt mehr, als klug wäre.",
  "Verkauft Routen kapitelweise.",
  "Zündet die Lampe beim Hinausgehen an.",
  "Landet besser, als sie startet.",
  "Absichtlich verlaufen, kommt trotzdem an.",
  "Zählt Geister, kennt einige beim Namen.",
  "Weiß, welche Tore lügen.",
  "Veteranin der grünen Hallen.",
  "Teilt die Feder, teilt den Aufstieg.",
  "Hat Meinungen zu Schriftrollen-Preisen.",
  "Skizziert Türen, die es noch nicht gibt.",
  "Fragt die Kerze, folgt dem Rauch.",
  "Hebt Knochen auf, stellt Laternen ab.",
  "Sieht doppelt. Springt trotzdem.",
  "Bleibt wegen der Rätsel.",
  "Ruhige Händlerin. Geduldige Wanderin.",
  "Benennt ihre Routen nach dem Wetter.",
  "Kauft Ruinen, verkauft Gerüchte.",
];

export const AUTHOR_BIO_TAGS_EN: readonly string[] = [
  "towers",
  "traders",
  "scrolls",
  "speedrun",
  "feather",
  "dbljump",
  "cartograph",
  "ruins",
  "nightwalk",
  "ghosts",
  "bargains",
  "puzzles",
  "explorer",
  "lanterns",
  "gates",
  "wanderer",
];

export const AUTHOR_BIO_TAGS_DE: readonly string[] = [
  "tuerme",
  "haendler",
  "rollen",
  "speedrun",
  "feder",
  "dplsprung",
  "kartograf",
  "ruinen",
  "nachtgang",
  "geister",
  "schnaepp",
  "raetsel",
  "erkunden",
  "laternen",
  "tore",
  "wanderer",
];

// Position-parity check — keep the two language arrays in lockstep. A
// mismatched length would silently shift the seed→entry mapping across
// languages.
if (AUTHOR_BIO_TAGLINES_EN.length !== AUTHOR_BIO_TAGLINES_DE.length) {
  throw new Error(
    `authorBioPool: tagline EN/DE length mismatch (${AUTHOR_BIO_TAGLINES_EN.length} vs ${AUTHOR_BIO_TAGLINES_DE.length})`,
  );
}
if (AUTHOR_BIO_TAGS_EN.length !== AUTHOR_BIO_TAGS_DE.length) {
  throw new Error(
    `authorBioPool: tag EN/DE length mismatch (${AUTHOR_BIO_TAGS_EN.length} vs ${AUTHOR_BIO_TAGS_DE.length})`,
  );
}

/** Total tagline pool size — pick an index in `[0, this)` and resolve via
 *  `authorBioPools(lang).taglines[i]`. */
export const AUTHOR_BIO_TAGLINE_COUNT = AUTHOR_BIO_TAGLINES_EN.length;

/** Total tag pool size — pick an index in `[0, this)` and resolve via
 *  `authorBioPools(lang).tags[i]`. */
export const AUTHOR_BIO_TAG_COUNT = AUTHOR_BIO_TAGS_EN.length;

export function authorBioPools(lang: Language): {
  taglines: readonly string[];
  tags: readonly string[];
} {
  return lang === "de"
    ? { taglines: AUTHOR_BIO_TAGLINES_DE, tags: AUTHOR_BIO_TAGS_DE }
    : { taglines: AUTHOR_BIO_TAGLINES_EN, tags: AUTHOR_BIO_TAGS_EN };
}
